import React, { useState, useEffect } from 'react';
import * as Page from '../../components/content/page-header';
import { Content } from '../../components/layout/content';
import { Label } from '../../components/forms/input';
import { DatePicker } from '../../components/forms/date';
import * as Buttons from '../../components/button/button';
import { Pageable, useGenericPagination } from '../../hooks/use-pagination';
import { FieldValues, useForm } from 'react-hook-form';
import { removeEmpty } from '../../utils/object-utils';
import { CellDateRenderer, CellRenderer } from '../../components/table/columns';
import { Job, JobStatus } from './types/job';
import { useQuery } from '../../hooks/use-query';
import { formatTime } from '../../utils/time';
import { Table } from '../../components/table/table';
import { Table as ReportsTable } from './components/table';
import { intervalToDuration } from 'date-fns';

type KeyValue = {
  [key: string]: string | number;
}

export const Jobs = (): JSX.Element => {
  const { handleSubmit, setValue, watch } = useForm();
  const [searchQuery, setSearchQuery] = useState<KeyValue>({});
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const job = useQuery<Job>({ path: `/jobs/${selectedJob?.id as string}`, options: { enabled: Boolean(selectedJob) } });
  const jobs = useGenericPagination<Pageable<Job>>('/jobs', searchQuery, { enabled: Boolean(searchQuery.startDate) });
  const [jobsList, setJobsList] = useState<Job[]>([]);
  const [isSubmitted, setSubmitted] = useState(false);
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  useEffect(() => {
    const existing = isSubmitted ? [] : jobsList;
    setJobsList([...existing, ...(jobs.data?.content || [])]);
    if (isSubmitted) {
      setSubmitted(false);
    }
  }, [jobs.data?.content]);

  const onSubmit = (fields: FieldValues): void => {
    if (searchQuery.startDate === fields.startDate && searchQuery.endDate === fields.endDate) {
      return;
    }
    setSearchQuery({ page: 0, ...removeEmpty(fields) });
    setSubmitted(true);
  };

  const jobStateColumns = [
    { accessor: 'status', Header: 'State', width: 58, maxWidth: 58, minWidth: 58, Cell: StatusRenderer },
    { accessor: 'startTime', Header: 'Date & Time', width: 202, Cell: CellDateRenderer },
    { accessor: 'description', Header: 'Process', Cell: CellRenderer },
  ];

  const stateDetailsColumns = [
    { accessor: 'type', Header: 'Type', sort: true, Cell: CellRenderer },
    { accessor: 'object', Header: 'Object', Cell: CellRenderer },
    { accessor: 'time', Header: 'Time', Cell: (item: any): JSX.Element => {
      const row = item.row.original as Job;
      return <div>{getDuration(row.startTime, row.endTime)}</div>;
    } },
    { accessor: 'error', Header: 'Error', Cell: CellRenderer },
    { accessor: 'details', Header: 'Details', Cell: CellRenderer },
  ];

  const onScrollEnd = (): void => {
    if (jobs.isFetching || jobs.data?.last) {
      return;
    }

    const currentPage = (searchQuery.page as number) || 0;
    setSearchQuery({ ...searchQuery, page: currentPage + 1 });
    if (isSubmitted) {
      setSubmitted(false);
    }
  };

  const onRowClick = (job: Job): void => {
    setSelectedJob(job);
  };

  return <Content>
    <Page.Header>
      <div className="mb-2">
        <Page.Title>Job Management</Page.Title>
      </div>
      <Page.SubTitle>
        View the status of jobs running within Command Post.
      </Page.SubTitle>
    </Page.Header>
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="row mb-5 align-items-end">
        <div className="col-4">
          <Label>Date From</Label>
          <div className="d-flex align-items-center">
            <DatePicker name="startDate" onChange={(date: string): void => setValue('startDate', date)} />
            <span className="d-flex ms-2">00:00</span>
          </div>
        </div>
        <div className="col-4">
          <Label>Date To</Label>
          <div className="d-flex align-items-center">
            <DatePicker name="endDate" onChange={(date: string): void => setValue('endDate', date)} />
            <span className="d-flex ms-2">00:00</span>
          </div>
        </div>
        <div className="col-4">
          <div className="ms-4">
            <Buttons.Primary type="submit" disabled={(jobs.isFetching && isSubmitted) || (!startDate || !endDate)}>Search</Buttons.Primary>
          </div>
        </div>
      </div>
    </form>
    <div className="row">
      <div className="col-12">
        <p><strong>Recent processes</strong></p>
      </div>
    </div>
    <ReportsTable columns={jobStateColumns} rows={jobsList} onScrollEnd={onScrollEnd} isLoading={jobs.isFetching && Boolean(jobsList.length)} onRowClick={onRowClick} />
    <div className="row mb-4 mt-4">
      <div className="col-3">
        <Label>Description</Label>
        <p>{job.data?.description}</p>
      </div>
      <div className="col-3">
        <Label>Initiator</Label>
        <p>{job.data?.initiator}</p>
      </div>
      <div className="col-3">
        <Label>Processed layers</Label>
        <p></p>
      </div>
      <div className="col-3">
        <Label>Processed services</Label>
        <p></p>
      </div>
    </div>
    <div className="row mb-4">
      <div className="col-3">
        <Label>Status</Label>
        <p>{job.data?.status}</p>
      </div>
      <div className="col-3">
        <Label>Duration</Label>
        <p>{getDuration(job.data?.startTime, job.data?.endTime)}</p>
      </div>
      <div className="col-3">
        <Label>Start time</Label>
        <p>{formatTime(job.data?.startTime)}</p>
      </div>
      <div className="col-3">
        <Label>End time</Label>
        <p>{formatTime(job.data?.endTime)}</p>
      </div>
    </div>
    <div className="row mb-4">
      <div className="col-8 d-flex align-items-end">
        <p className="m-0">
          <strong>Errors registered during the polling</strong>
        </p>
      </div>
      {/* <div className="col-4 d-flex justify-content-end">*/}
      {/*  <Page.Search />*/}
      {/* </div>*/}
    </div>
    <Table columns={stateDetailsColumns} rows={job.data?.steps || []} />
  </Content>;
};

const StatusRenderer = (props: any): JSX.Element => {
  const status = props.value?.toUpperCase() as JobStatus;
  switch(status) {
    case JobStatus.PENDING:
      return <div className="job-state-cell yellow">P</div>;
    case JobStatus.RUNNING:
      return <div className="job-state-cell green">R</div>;
    case JobStatus.CANCELED:
      return <div className="job-state-cell gray">C</div>;
    case JobStatus.COMPLETED:
      return <div className="job-state-cell green">C</div>;
    case JobStatus.INTERRUPTED:
      return <div className="job-state-cell red">I</div>;
    case JobStatus.STOPPING:
      return <div className="job-state-cell red">S</div>;
    case JobStatus.FAILED:
      return <div className="job-state-cell red">F</div>;
    case JobStatus.FINISHED:
      return <div className="job-state-cell black">F</div>;
    default:
      return <div />;
  }
};

type Duration = {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const getDuration = (startTime?: string, endTime?: string): string => {
  if (!startTime || !endTime) {
    return '';
  }

  const duration = intervalToDuration({ start: new Date(startTime), end: new Date(endTime) }) as Duration;
  let durationString = '';
  for (const key in duration) {
    const value = duration[key as keyof Duration];
    if (value === 0) {
      continue;
    }
    if (durationString) {
      durationString += ' ';
    }
    durationString += `${value} ${value === 1 ? key.slice(0, key.length - 1) : key}`;
  }
  return durationString;
};
